import { JobStatusPill } from 'components/FbUI/JobStatusPill'
import { RequestedContactsTable } from 'components/Tables/RequestedContactsTable/RequestedContactsTable'
import { Modal } from 'components/UI/Modal/Modal'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import React, { useEffect, useMemo, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import useApiService from 'services/api'
import { ContactRequestJobDetailResponse } from 'services/contactRequest'

interface ContactRequestJobDetailsModalProps {
  open: boolean
  onClose: () => void
  jobId: number | null
}

export const ContactRequestJobDetailsModal: React.FC<
  ContactRequestJobDetailsModalProps
> = ({ open, onClose, jobId }) => {
  const api = useApiService()
  const [jobDetails, setJobDetails] =
    useState<ContactRequestJobDetailResponse | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)

  useEffect(() => {
    if (jobId !== null && open) {
      const fetchJobDetails = async () => {
        setIsLoading(true)
        try {
          const response = await api.fetchContactRequestJobDetails(jobId)
          setJobDetails(response)
        } catch (error) {
          console.error('Error fetching job details:', error)
        } finally {
          setIsLoading(false)
        }
      }
      fetchJobDetails()
    }
  }, [jobId, open])

  const uniqueCompaniesCount = useMemo(() => {
    return jobDetails?.unique_companies_count ?? 0
  }, [jobDetails])

  return (
    <Modal
      size="2xl"
      open={open}
      onOpenChange={onClose}
      title={
        <div className="flex flex-col items-start gap-1.5">
          {jobDetails?.status && (
            <JobStatusPill
              statusValue={jobDetails.status.id}
              statusLabel={jobDetails.status.label}
            />
          )}
          <p className="text-xl font-semibold text-neutral-900 leading-7">
            Requested Contacts
          </p>
          {jobDetails?.campaign?.name && (
            <p className="text-sm font-medium text-neutral-500">
              {jobDetails.campaign.name}
            </p>
          )}
        </div>
      }
      childrenContainerClassName="max-h-[70vh] overflow-y-auto overflow-x-hidden max-w-full w-full pt-3"
      dialogFooterClassName="flex !justify-center sm:!justify-center"
      cancelButtonText="Close"
      cancelButtonProps={{
        className:
          '!flex-none !min-w-[140px] !bg-brand-bright !text-white hover:!bg-brand-active !px-6',
      }}
    >
      {isLoading ? (
        <div className="flex justify-center items-center h-48">
          <Spinner animation="border" />
        </div>
      ) : jobDetails ? (
        <div>
          <hr className="h-px mb-4 bg-gray-300 border-0 dark:bg-gray-700"></hr>
          <div className="grid grid-cols-3 gap-6">
            <div className="space-y-1.5">
              <h3 className="text-sm font-medium text-neutral-500 mb-2">
                Details
              </h3>
              <p>
                <span className="font-bold">Requested by:</span>{' '}
                {jobDetails.user.first_name} {jobDetails.user.last_name}
              </p>
              <p>
                <span className="font-bold">Created:</span>{' '}
                {new Date(jobDetails.created).toLocaleString()}
              </p>
              <p>
                <span className="font-bold">Last Updated:</span>{' '}
                {new Date(jobDetails.modified).toLocaleString()}
              </p>
            </div>
            <div className="space-y-1.5">
              <h3 className="text-sm font-medium text-neutral-500 mb-2">
                Unique Companies
              </h3>
              <p>
                <span className="font-bold">Requested:</span>{' '}
                {uniqueCompaniesCount}
              </p>
              <p>
                <span className="font-bold">Found:</span>{' '}
                {jobDetails.found_companies_count}
              </p>
            </div>
            <div className="space-y-1.5">
              <h3 className="text-sm font-medium text-neutral-500 mb-2">
                Contacts
              </h3>
              <p>
                <span className="font-bold">Max per company:</span>{' '}
                {jobDetails.max_contacts}
              </p>
              <p>
                <span className="font-bold">Requested:</span>{' '}
                {jobDetails.max_contacts * uniqueCompaniesCount}
              </p>
              <p>
                <span className="font-bold">Found:</span>{' '}
                {jobDetails.found_active_count || 0}
              </p>
            </div>
          </div>
          <hr className="h-px my-4 bg-gray-300 border-0 dark:bg-gray-700"></hr>
          <div className="mt-3">
            <p className="text-sm font-medium text-neutral-500 mb-2">
              Requested Contacts (
              {jobDetails.max_contacts * uniqueCompaniesCount})
            </p>
            {jobId && (
              <TableLinkStyleProvider variant="plain">
                <div
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: 8,
                    background: '#ffffff',
                    overflow: 'hidden',
                    boxShadow:
                      '0px 1px 3px 0px rgba(16, 24, 40, 0.08), 0px 1px 2px -1px rgba(16, 24, 40, 0.04)',
                  }}
                >
                  <RequestedContactsTable jobId={jobId} height={370} />
                </div>
              </TableLinkStyleProvider>
            )}
          </div>
        </div>
      ) : (
        <p>No job details available.</p>
      )}
    </Modal>
  )
}
